/**
 * Verification registration ID options by headquarters country.
 * Multi-option major markets use existing RegistrationIdType values
 * (LEI, COUNTRY_REGISTRATION, LOCAL_REGISTRY) with country-specific UI labels.
 *
 * Dedicated paths (India Gridlines, UK CRN, TW UBN, NO NOGN, FR SIREN, EU VAT)
 * are resolved in getRegistrationTypeOptionsForHeadquarters.
 */

export type RegistrationIdOption = {
  value: string;
  label: string;
};

export type RegistrationOptionsContext = {
  getRegistrationNameByCountry: (hq: string) => string | null;
  supportsEuVat: (hq: string) => boolean;
  getEuVatLabel: (hq: string) => string;
  usesIndirectCountryRegistration: (hq: string) => boolean;
};

const LEI_OPTION: RegistrationIdOption = {
  value: "LEI",
  label: "LEI (Preferred)",
};

/**
 * Major markets: non-LEI options (primary company ID → COUNTRY_REGISTRATION,
 * secondary tax/trade ID → LOCAL_REGISTRY).
 */
export const MAJOR_MARKET_REGISTRY_OPTIONS: Readonly<
  Record<string, readonly RegistrationIdOption[]>
> = {
  Switzerland: [
    { value: "COUNTRY_REGISTRATION", label: "UID (CHE company number)" },
    { value: "LOCAL_REGISTRY", label: "VAT number (MWST/TVA/IVA)" },
  ],
  Pakistan: [
    { value: "COUNTRY_REGISTRATION", label: "SECP Company Registration Number" },
    { value: "LOCAL_REGISTRY", label: "NTN (National Tax Number)" },
  ],
  Ghana: [
    { value: "COUNTRY_REGISTRATION", label: "Registrar-General Number" },
    { value: "LOCAL_REGISTRY", label: "TIN (Tax Identification Number)" },
  ],
  Argentina: [
    { value: "COUNTRY_REGISTRATION", label: "CUIT" },
    { value: "LOCAL_REGISTRY", label: "IGJ / company registry number" },
  ],
  Chile: [{ value: "COUNTRY_REGISTRATION", label: "RUT" }],
  Colombia: [{ value: "COUNTRY_REGISTRATION", label: "NIT" }],
  Peru: [{ value: "COUNTRY_REGISTRATION", label: "RUC" }],
  Ecuador: [{ value: "COUNTRY_REGISTRATION", label: "RUC" }],
  Uruguay: [{ value: "COUNTRY_REGISTRATION", label: "RUT / RUC" }],
  Ukraine: [{ value: "COUNTRY_REGISTRATION", label: "EDRPOU" }],
  Serbia: [{ value: "COUNTRY_REGISTRATION", label: "Matični broj (MB)" }],
  Iceland: [{ value: "COUNTRY_REGISTRATION", label: "Kennitala" }],
  Kazakhstan: [{ value: "COUNTRY_REGISTRATION", label: "BIN" }],
  Cambodia: [{ value: "COUNTRY_REGISTRATION", label: "Commercial Registration Number" }],
  Laos: [{ value: "COUNTRY_REGISTRATION", label: "Enterprise Registration Number" }],
  "Burma (Myanmar)": [
    { value: "COUNTRY_REGISTRATION", label: "DICA Registration Number" },
  ],
  Jordan: [{ value: "COUNTRY_REGISTRATION", label: "National Company Number" }],
  Lebanon: [{ value: "COUNTRY_REGISTRATION", label: "Commercial Register Number" }],
  Ethiopia: [
    { value: "COUNTRY_REGISTRATION", label: "Business registration number" },
    { value: "LOCAL_REGISTRY", label: "TIN (Tax Identification Number)" },
  ],
  Tanzania: [{ value: "COUNTRY_REGISTRATION", label: "BRELA Company Number" }],
  Uganda: [{ value: "COUNTRY_REGISTRATION", label: "URSB Registration Number" }],
  Rwanda: [{ value: "COUNTRY_REGISTRATION", label: "RDB Company Code" }],
  Senegal: [{ value: "COUNTRY_REGISTRATION", label: "NINEA" }],
  "Ivory Coast": [{ value: "COUNTRY_REGISTRATION", label: "RCCM / DFE" }],
  Tunisia: [{ value: "COUNTRY_REGISTRATION", label: "RNE Number" }],
  Algeria: [
    { value: "COUNTRY_REGISTRATION", label: "NRC (Registre de Commerce)" },
    { value: "LOCAL_REGISTRY", label: "NIF (Numéro d'Identification Fiscale)" },
  ],
  Panama: [{ value: "COUNTRY_REGISTRATION", label: "RUC" }],
  "Costa Rica": [{ value: "COUNTRY_REGISTRATION", label: "Cédula jurídica" }],
  Nigeria: [
    { value: "COUNTRY_REGISTRATION", label: "RC Number (CAC)" },
    { value: "LOCAL_REGISTRY", label: "TIN (Tax Identification Number)" },
  ],
};

function withLei(options: readonly RegistrationIdOption[]): RegistrationIdOption[] {
  return [LEI_OPTION, ...options.map((o) => ({ value: o.value, label: o.label }))];
}

/** Primary company-registry label for HQ (stored as registrationName on profile). */
export function getPrimaryRegistrationLabel(hq: string | null | undefined): string | null {
  const key = hq?.trim() ?? "";
  if (!key) return null;
  const options = MAJOR_MARKET_REGISTRY_OPTIONS[key];
  if (!options?.length) return null;
  const primary = options.find((o) => o.value === "COUNTRY_REGISTRATION") ?? options[0];
  return primary?.label ?? null;
}

/** Secondary ID label when LOCAL_REGISTRY is offered for this HQ. */
export function getSecondaryRegistrationLabel(hq: string | null | undefined): string | null {
  const key = hq?.trim() ?? "";
  if (!key) return null;
  const options = MAJOR_MARKET_REGISTRY_OPTIONS[key];
  if (!options?.length) return null;
  const secondary = options.find((o) => o.value === "LOCAL_REGISTRY");
  return secondary?.label ?? null;
}

/**
 * All verification Select options for the given headquarters country.
 * Pass context so EU VAT / registration name helpers stay package-local.
 */
export function getRegistrationTypeOptionsForHeadquarters(
  headquartersCountry: string | null | undefined,
  ctx: RegistrationOptionsContext,
): RegistrationIdOption[] {
  const hq = headquartersCountry?.trim() ?? "";
  if (!hq) return [];

  if (hq === "India") {
    return [
      LEI_OPTION,
      { value: "GST", label: "GST" },
      { value: "CIN", label: "CIN" },
      { value: "UDM", label: "UDM (Udyam)" },
    ];
  }

  const officialLabel =
    ctx.getRegistrationNameByCountry(hq) || "Official company registration ID";

  if (hq === "Taiwan") {
    return withLei([
      { value: "UBN", label: officialLabel || "Unified Business Number (UBN)" },
    ]);
  }

  if (hq === "United Kingdom") {
    return withLei([
      {
        value: "CRN",
        label: officialLabel || "Company Registration Number (CRN)",
      },
    ]);
  }

  if (hq === "Norway") {
    return withLei([
      { value: "NOGN", label: officialLabel || "Organisation Number" },
    ]);
  }

  if (hq === "France") {
    return [
      LEI_OPTION,
      { value: "EU_VAT", label: ctx.getEuVatLabel(hq) },
      { value: "SIREN", label: officialLabel || "SIREN / SIRET" },
    ];
  }

  if (ctx.supportsEuVat(hq)) {
    return [LEI_OPTION, { value: "EU_VAT", label: ctx.getEuVatLabel(hq) }];
  }

  const major = MAJOR_MARKET_REGISTRY_OPTIONS[hq];
  if (major?.length) {
    return withLei(major);
  }

  const isNewZealand = hq === "New Zealand";
  const isChinaMainland = hq === "China (Mainland)";
  const isIndirect = ctx.usesIndirectCountryRegistration(hq);

  const registryValue = isNewZealand
    ? "NZBN"
    : isChinaMainland
      ? isIndirect
        ? "COUNTRY_REGISTRATION"
        : "USCC"
      : isIndirect
        ? "COUNTRY_REGISTRATION"
        : "LOCAL_REGISTRY";

  return withLei([{ value: registryValue, label: officialLabel }]);
}
